import { CloseOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Modal, Table, Input } from 'antd'
import React, { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'

import ExcelButton from '@components/ExcelButton'
import { http } from '@services/http'
import { getToday } from '@utils/getDays'
import { numberWithSpacesIntl } from '@utils/numberWithSpaces'

const ActSverkaForOthers = ({ visible, datas, onClose }) => {
	const today = getToday()

	const { t } = useTranslation()
	const pdfRef = useRef(null)

	const [startDate, setStartDate] = useState('2024-01-01')
	const [endDate, setEndDate] = useState(today)
	const [cardCode, setCardCode] = useState('')
	const [loadingExcel, setLoadingExcel] = useState(false)

	const columns = [
		{
			title: t('date'),
			dataIndex: 'dueDate',
			key: 'dueDate',
		},
		{
			title: t('numClient'),
			dataIndex: 'docNum',
			key: 'docNum',
			render: (text) => {
				const formattedText = numberWithSpacesIntl(text)
				return `${formattedText}`
			},
		},
		{
			title: t('lineMemo'),
			dataIndex: 'lineMemo',
			key: 'lineMemo',
		},

		{
			title: 'Debit',
			dataIndex: 'debit',
			key: 'debit',
			render: (text) => {
				const formattedText = numberWithSpacesIntl(text)
				return `${formattedText}`
			},
		},
		{
			title: 'Credit',
			dataIndex: 'credit',
			key: 'credit',
			render: (text) => {
				const formattedText = numberWithSpacesIntl(text)
				return `${formattedText}`
			},
		},
		{
			title: t('balanceClient'),
			dataIndex: 'cumulativeBalance',
			key: 'cumulativeBalance',
			render: (text) => {
				const formattedText = numberWithSpacesIntl(text)
				return `${formattedText}`
			},
		},
	]

	useEffect(() => {
		const cardCode = datas?.cardCode
		setCardCode(cardCode)
	}, [datas, visible])

	const fetchClientInfo = async ({ queryKey }) => {
		const [_, cardCode, startDate, endDate] = queryKey
		try {
			const { data } = await http.get(
				`api/businesspartners/info?cardCode=${cardCode}&startDate=${startDate}&endDate=${endDate}`,
			)

			return data
		} catch (error) {
			console.error('Error fetching data:', error)
		}
	}

	const { data, isLoading: loading } = useQuery({
		queryKey: ['akt-sverka', cardCode, startDate, endDate],
		queryFn: fetchClientInfo,
		keepPreviousData: true,
		enabled: !!cardCode,
		refetchOnWindowFocus: true,
		refetchOnMount: true,
		refetchOnReconnect: true,
	})

	const formattedData = data?.businessPartnerInfoLines

	const balance = data && numberWithSpacesIntl(data?.balance)

	const balanceFirstDayOfPeriod = numberWithSpacesIntl(
		data?.balanceFirstDayOfPeriod,
	)

	const handleExportExcel = () => {
		if (!data) return

		setLoadingExcel(true)

		try {
			const sheetData = [
				[t('supplier'), data?.cardName || ''],
				[t('startDate'), startDate],
				[t('endDate'), endDate],
				[t('balance'), balance || ''],
				[t('balanceFirstDayOfPeriod'), balanceFirstDayOfPeriod || ''],
				[],
				[
					t('date'),
					t('numClient'),
					t('lineMemo'),
					'Debit',
					'Credit',
					t('balanceClient'),
				],
				...((formattedData || []).map((item) => [
					item.dueDate,
					numberWithSpacesIntl(item.docNum),
					item.lineMemo,
					numberWithSpacesIntl(item.debit),
					numberWithSpacesIntl(item.credit),
					numberWithSpacesIntl(item.cumulativeBalance),
				])),
			]

			const workbook = XLSX.utils.book_new()
			const worksheet = XLSX.utils.aoa_to_sheet(sheetData)
			worksheet['!cols'] = Array(6).fill({ wpx: 180 })
			XLSX.utils.book_append_sheet(workbook, worksheet, 'AktSverka')

			const workbookOut = XLSX.write(workbook, {
				bookType: 'xlsx',
				type: 'array',
			})

			const blob = new Blob([workbookOut], {
				type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			})
			const fileUrl = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			const safeName = (data?.cardName || 'AktSverka')
				.replace(/[/\?%*:|"<>]/g, '')
				.trim()
			const fileName = `${safeName}(${startDate}-${endDate}).xlsx`

			link.href = fileUrl
			link.setAttribute('download', fileName)
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(fileUrl)
		} catch (error) {
			console.error('Error exporting Excel:', error)
		} finally {
			setLoadingExcel(false)
		}
	}

	return (
		<Modal
			title={
				<h1 className="font-nunito text-xl font-extrabold text-[#000000]">
					{t('supplier')}: {data?.cardName || ''}
				</h1>
			}
			visible={visible}
			onCancel={onClose}
			footer={null}
			closeIcon={<CloseOutlined />}
			width="85%"
			style={{ height: '85%' }}
			bodyStyle={{ height: 'calc(100% - 55px)', overflowY: 'auto' }}
		>
			<div className="w-full px-2" ref={pdfRef}>
				<div className="mt-4 flex flex-col justify-between sm:mt-10 sm:flex-row">
					<div className="flex flex-col gap-3">
						<p className="font-nunito">{t('supplier')}</p>
						<Input
							type="text"
							style={{ color: 'black' }}
							value={data?.cardName}
							className="flex h-8 w-full items-center justify-center sm:w-[250px]"
							disabled
						/>
					</div>
					<div className="ml-10 mt-5 flex gap-5">
						<div className="flex flex-col gap-3">
							<p>{t('startDate')}</p>
							<Input
								type="date"
								value={startDate}
								className="w-[150px]"
								onChange={(e) => { setStartDate(e.target.value) }}
								max={endDate}
							/>
						</div>
						<div className="flex flex-col gap-3">
							<p>{t('endDate')}</p>
							<Input
								type="date"
								value={endDate}
								className="w-[150px]"
								onChange={(e) => { setEndDate(e.target.value) }}
								min={startDate}
								max={today}
							/>
						</div>
					</div>
				</div>

				<div className="mt-4 flex flex-col justify-between sm:mt-10 sm:flex-row">
					<div className="flex flex-col gap-3">
						<p className="font-nunito">{t('balance')}</p>
						<Input
							type="text"
							style={{ color: 'black' }}
							value={balance}
							className="flex h-8 w-full items-center justify-center sm:w-[250px]"
							disabled
						/>
					</div>
					<div className="flex flex-col gap-3">
						<div className="flex flex-col gap-3">
							<p className="font-nunito">{t('balanceFirstDayOfPeriod')}</p>
							<Input
								type="text"
								style={{ color: 'black' }}
								value={balanceFirstDayOfPeriod}
								className="flex h-8 w-full items-center justify-center sm:w-[250px]"
								disabled
							/>
						</div>
						<div className="flex flex-col justify-end">
							<ExcelButton
								handleDownloadExcel={handleExportExcel}
								loadingExcel={loadingExcel}
							/>
						</div>
					</div>
				</div>

				<Table
					columns={columns}
					dataSource={formattedData}
					pagination={false}
					loading={loading}
					className="border-1 mt-12 border-black"
					summary={(pageData) => {
						const totalDebit = pageData.reduce((total, item) => total + +item.debit, 0)
						const totalCredit = pageData.reduce((total, item) => total + item.credit, 0)
						return (
							<Table.Summary fixed="bottom">
								<Table.Summary.Row>
									<Table.Summary.Cell index={0} colSpan={3} className="font-bold text-black">
									</Table.Summary.Cell>
									<Table.Summary.Cell index={1} className="font-bold text-black">
										{numberWithSpacesIntl(totalDebit)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={2} className="font-bold text-black">
										{numberWithSpacesIntl(totalCredit)}
									</Table.Summary.Cell>
									<Table.Summary.Cell index={3} className="font-bold text-black">
									</Table.Summary.Cell>
								</Table.Summary.Row>
							</Table.Summary>
						)
					}}
				/>
			</div>
		</Modal>
	)
}

export default ActSverkaForOthers
